// Figma: "Invoices / All Communities / Expanded" and the rest of the canvas under node
// 18698:9037 ("Invoice") — All Communities / Collapsed, Filtered Communities, Single
// Community, Community Dropdown, Year Dropdown, No Results (both All/Single Community
// variants), Error State, and Download Success/Error (the "Toast" instance shown after a
// download). This replaces DashboardPage.js's placeholder mapping of the sidebar's
// "Invoices" destination onto the CRM tab's accordion-based Invoice component (see that
// file's Crm/Invoice.js — still used as-is inside the CRM tab) with the dedicated table
// this Figma page actually specifies: communities as collapsible groups, each expanding
// into its available months, filterable by Year and by Community, with a per-row download.
//
// Data comes from the same (already-ported) `getCurrentInvoicesList`/`getPastInvoicesList`
// endpoints Crm/Invoice.js uses — see invoiceUtils.js's header comment for how their
// year/month/community shape is inverted into this page's community/month grouping. Same
// non-admin/non-Administrator community filtering as Crm/Invoice.js (against
// `dashboardCommunities`), and the same shared blob-download flow (commons/utils/
// invoiceDownload.js), here surfaced as a toast instead of a silent download.
import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {Box, ButtonBase, Stack, Typography} from '@mui/material';

import {useLazyGetCurrentInvoicesListQuery, useLazyGetPastInvoicesListQuery} from '../../../features/coreApi/apiSlice';
import {getDashboardAccount, getDashboardCommunities, getDashboardCommunity} from '../../../app/store/reducers/dashboard';
import {getIsStakeAdmin, getTeamMemberRole} from '../../../app/store/reducers/session';
import {downloadInvoiceFile} from '../../../commons/utils/invoiceDownload';
import LoadingBox from '../../../commons/components/LoadingBox';
import {tokens} from '../../../themes/ThemeCustomization';
import {buildCommunityGroups, getAvailableYears} from './invoiceUtils';
import CommunityFilterDropdown from './CommunityFilterDropdown';
import YearFilterDropdown from './YearFilterDropdown';
import InvoiceToast from './InvoiceToast';

import invoiceRowChevronIcon from '../../../assets/shell/invoice-row-chevron-icon.svg';
import invoiceDownloadIcon from '../../../assets/shell/invoice-download-icon.svg';
import invoiceEmptyIcon from '../../../assets/shell/invoice-empty-icon.svg';
import invoiceErrorIcon from '../../../assets/shell/invoice-error-icon.svg';

const StateCard = ({icon, alt, title, description}) => (
    <Box sx={{border: `1px solid ${tokens.color.neutral200}`, borderRadius: '6px', width: '100%'}}>
        <Stack alignItems="center" justifyContent="center" spacing={3} sx={{height: 300, p: 2.5}}>
            <Box sx={{border: `1px solid ${tokens.color.neutral200}`, borderRadius: '6px', p: 2, display: 'flex'}}>
                <Box component="img" src={icon} alt={alt} sx={{width: 24, height: 24}} />
            </Box>
            <Stack spacing={0.25} alignItems="center" sx={{textAlign: 'center', pt: 1, pb: 2.5}}>
                <Typography variant="body2" fontWeight={500}>
                    {title}
                </Typography>
                <Typography variant="caption" sx={{color: tokens.color.neutral600}}>
                    {description}
                </Typography>
            </Stack>
        </Stack>
    </Box>
);

const MonthRow = ({month, path, onDownload, indent}) => (
    <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{height: 52, borderTop: `1px solid ${tokens.color.neutral200}`, pl: indent ? 3 : 2, pr: 2}}
    >
        <Typography variant="body2">{month}</Typography>
        <ButtonBase onClick={() => onDownload(path)} sx={{p: 0.5, borderRadius: '8px'}}>
            <Box component="img" src={invoiceDownloadIcon} alt="Download" sx={{width: 16, height: 16}} />
        </ButtonBase>
    </Stack>
);

const CommunityGroup = ({name, months, expanded, onToggle, onDownload}) => (
    <>
        <ButtonBase
            onClick={onToggle}
            sx={{
                width: '100%',
                height: 52,
                justifyContent: 'flex-start',
                gap: 1,
                px: 2,
                borderTop: `1px solid ${tokens.color.neutral200}`
            }}
        >
            <Box
                component="img"
                src={invoiceRowChevronIcon}
                alt=""
                sx={{width: 16, height: 16, transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s'}}
            />
            <Typography variant="body2" fontWeight={500}>
                {name}
            </Typography>
        </ButtonBase>
        {expanded && months.map((entry) => <MonthRow key={entry.path} month={entry.month} path={entry.path} onDownload={onDownload} indent />)}
    </>
);

const InvoicesSection = () => {
    const dashboardAccount = useSelector(getDashboardAccount);
    const dashboardCommunity = useSelector(getDashboardCommunity);
    const dashboardCommunities = useSelector(getDashboardCommunities);
    const teamMemberRole = useSelector(getTeamMemberRole);
    const isStakeAdmin = useSelector(getIsStakeAdmin);

    const [fetchCurrentInvoices, {data: currentInvoices = [], isLoading: isCurrentLoading, isFetching: isCurrentFetching, isError: isCurrentError}] =
        useLazyGetCurrentInvoicesListQuery();
    const [fetchPastInvoices, {data: pastInvoices = [], isLoading: isPastLoading, isFetching: isPastFetching, isError: isPastError}] =
        useLazyGetPastInvoicesListQuery();

    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedCommunities, setSelectedCommunities] = useState([]);
    const [expandedCommunities, setExpandedCommunities] = useState([]);
    const [toast, setToast] = useState(null);

    const isSingleCommunity = Boolean(dashboardCommunity?.sfid) && dashboardCommunity.sfid !== 'all';

    useEffect(() => {
        if (dashboardAccount?.sfid) {
            const community_sfid = isSingleCommunity ? dashboardCommunity.sfid : null;
            fetchCurrentInvoices({sfid: dashboardAccount.sfid, community_sfid});
            fetchPastInvoices({sfid: dashboardAccount.sfid, community_sfid});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardAccount, dashboardCommunity]);

    // Switching account/community makes the previous filter/expansion selection stale.
    useEffect(() => {
        setSelectedYear(new Date().getFullYear());
        setSelectedCommunities([]);
        setExpandedCommunities([]);
    }, [dashboardAccount?.sfid, dashboardCommunity?.sfid]);

    const years = useMemo(() => getAvailableYears(pastInvoices), [pastInvoices]);

    const communityGroups = useMemo(
        () => buildCommunityGroups({pastInvoices, currentInvoices, selectedYear}),
        [pastInvoices, currentInvoices, selectedYear]
    );

    // Same non-admin/non-Administrator community filtering as Crm/Invoice.js's InvoiceList.
    const permissionFilteredNames = useMemo(() => {
        const allNames = Array.from(communityGroups.keys());
        if (isStakeAdmin || teamMemberRole === 'Administrator') return allNames;
        const allowedNames = new Set(dashboardCommunities.map((community) => community.name));
        return allNames.filter((name) => allowedNames.has(name));
    }, [communityGroups, isStakeAdmin, teamMemberRole, dashboardCommunities]);

    const visibleCommunityNames = useMemo(() => {
        const sorted = [...permissionFilteredNames].sort((a, b) => a.localeCompare(b));
        if (selectedCommunities.length === 0) return sorted;
        return sorted.filter((name) => selectedCommunities.includes(name));
    }, [permissionFilteredNames, selectedCommunities]);

    const isLoading = isCurrentLoading || isCurrentFetching || isPastLoading || isPastFetching;
    const isError = isCurrentError || isPastError;

    const handleDownload = (path) => {
        downloadInvoiceFile(path)
            .then(() =>
                setToast({
                    type: 'success',
                    title: 'Invoice downloaded',
                    description: 'Your invoice is ready and has been downloaded successfully.'
                })
            )
            .catch(() =>
                setToast({
                    type: 'error',
                    title: 'Download failed',
                    description: 'Unable to download the invoice. Please try again.'
                })
            );
    };

    const toggleCommunity = (name) => {
        setExpandedCommunities((prev) => (prev.includes(name) ? prev.filter((entry) => entry !== name) : [...prev, name]));
    };

    // Same non-admin/non-Administrator gate as the permission filter above, applied to the
    // single selected community — matches Crm/Invoice.js's InvoiceList, which re-checks
    // `dashboardCommunities` even when already community-scoped by the API call.
    const singleCommunityAllowed = useMemo(() => {
        if (!isSingleCommunity) return true;
        if (isStakeAdmin || teamMemberRole === 'Administrator') return true;
        return dashboardCommunities.some((community) => community.name === dashboardCommunity.name);
    }, [isSingleCommunity, isStakeAdmin, teamMemberRole, dashboardCommunities, dashboardCommunity]);

    // Single-community mode is already scoped server-side by community_sfid, so every group
    // returned belongs to the one selected community — flatten straight to its months.
    const singleCommunityMonths = useMemo(() => {
        if (!singleCommunityAllowed) return [];
        const [months] = communityGroups.values();
        return months || [];
    }, [communityGroups, singleCommunityAllowed]);

    let content;
    if (isLoading) {
        content = <LoadingBox height={300} />;
    } else if (isError) {
        content = <StateCard icon={invoiceErrorIcon} alt="" title="Something went wrong" description="We couldn't load the invoices. Please try again later." />;
    } else if (isSingleCommunity) {
        content =
            singleCommunityMonths.length === 0 ? (
                <StateCard
                    icon={invoiceEmptyIcon}
                    alt=""
                    title="No invoices found"
                    description="There are no invoices available for this community."
                />
            ) : (
                <Box sx={{width: '100%'}}>
                    {singleCommunityMonths.map((entry) => (
                        <MonthRow key={entry.path} month={entry.month} path={entry.path} onDownload={handleDownload} />
                    ))}
                </Box>
            );
    } else {
        content =
            visibleCommunityNames.length === 0 ? (
                <StateCard icon={invoiceEmptyIcon} alt="" title="No invoices found" description="There are no invoices available at this time." />
            ) : (
                <Box sx={{width: '100%'}}>
                    {visibleCommunityNames.map((name) => (
                        <CommunityGroup
                            key={name}
                            name={name}
                            months={communityGroups.get(name) || []}
                            expanded={expandedCommunities.includes(name)}
                            onToggle={() => toggleCommunity(name)}
                            onDownload={handleDownload}
                        />
                    ))}
                </Box>
            );
    }

    return (
        <>
            <Box
                sx={{
                    border: `1px solid ${tokens.color.neutral200}`,
                    borderRadius: tokens.radius.toast,
                    backgroundColor: tokens.color.white,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <Stack direction="row" spacing={1} sx={{p: 2}}>
                    {!isSingleCommunity && (
                        <CommunityFilterDropdown options={permissionFilteredNames.slice().sort((a, b) => a.localeCompare(b))} selected={selectedCommunities} onChange={setSelectedCommunities} />
                    )}
                    <YearFilterDropdown years={years} selectedYear={selectedYear} onChange={setSelectedYear} />
                </Stack>
                {content}
            </Box>
            <InvoiceToast toast={toast} onClose={() => setToast(null)} />
        </>
    );
};

export default InvoicesSection;
